import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../lib/api";
import LabBookingLayout from "../components/LabBookingLayout";
import { useLabBooking } from "../context/LabBookingContext";

const APP_ID      = 1; // END_USER_APP
const PLATFORM_ID = 3; // WEB

const GENDER_OPTIONS = ["Male", "Female", "Other"];

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, deviceId, pendingLabBooking, setPendingLabBooking, setSelectedLab } = useLabBooking();

  // step: "mobile" → "otp" → "profile" (only for incomplete accounts) → done
  const [step, setStep]           = useState("mobile");
  const [mobile, setMobile]       = useState("");
  const [otp, setOtp]             = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  // Temporarily hold the verified user data while the profile step is shown.
  const [verifiedUser, setVerifiedUser] = useState(null);

  // Profile form — only shown for new/incomplete accounts.
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    gender: "Male",
    dob: "",
  });

  // User landed here already logged in but abandoned the profile step earlier.
  // Jump straight to profile — no need to re-verify OTP.
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("lab_booking_user") || "null");
    if (storedUser && !storedUser.first_name) {
      setVerifiedUser(storedUser);
      setStep("profile");
    }
  }, []);

  const handleSendOtp = () => {
    if (mobile.trim().length !== 10) return;
    setLoading(true);
    setError(null);
    api
      .post("/user/get-otp", { mobile, app_id: APP_ID })
      .then((res) => {
        if (res.data?.code === 200) setStep("otp");
        else setError(res.data?.message || "Couldn't send OTP, try again.");
      })
      .catch(() => setError("Couldn't send OTP, try again."))
      .finally(() => setLoading(false));
  };

  // After profile is saved (or skipped for existing users), proceed with booking.
  const proceedAfterLogin = (userData) => {
    login(userData);

    if (pendingLabBooking) {
      const { lab, testIds, collectionType } = pendingLabBooking;
      api
        .post("/user/lab/cart", {
          lab_id: lab.lab_id,
          test_ids: testIds,
          test_collection_type: collectionType === "lab" ? 1 : 2,
        })
        .then((cartRes) => {
          setPendingLabBooking(null);
          if (cartRes.data?.code === 200) {
            setSelectedLab({ lab: cartRes.data.data.lab, testList: cartRes.data.data.test_list, homeCollectionCharge: lab.home_collection_charge });
            navigate("/lab-booking/cart");
          } else {
            navigate("/lab-booking/compare");
          }
        })
        .catch(() => {
          setPendingLabBooking(null);
          navigate("/lab-booking/compare");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      navigate("/lab-booking/patient-details");
    }
  };

  const handleVerify = () => {
    if (otp.trim().length !== 4) return;
    setLoading(true);
    setError(null);
    api
      .post("/user/login", { mobile, otp, device_id: deviceId, app_id: APP_ID, platform_id: PLATFORM_ID })
      .then((res) => {
        if (res.data?.code !== 200) {
          setError(res.data?.message || "Couldn't verify OTP, try again.");
          setLoading(false);
          return;
        }
        const userData = res.data.data;

        // If user has no first_name, they're new (or used app without completing profile).
        // Show the profile step so the booking has complete patient data.
        if (!userData.first_name) {
          setVerifiedUser(userData);
          // Set the token now so /user/update is authenticated.
          // We call login() properly after profile save, but we need the token
          // in localStorage for the /user/update call — set it directly.
          localStorage.setItem("lab_booking_token", userData.access_token);
          localStorage.setItem("lab_booking_user", JSON.stringify(userData));
          setStep("profile");
          setLoading(false);
        } else {
          proceedAfterLogin(userData);
        }
      })
      .catch(() => {
        setError("Couldn't verify OTP, try again.");
        setLoading(false);
      });
  };

  const handleSaveProfile = () => {
    if (!profile.first_name.trim() || !profile.last_name.trim()) {
      setError("First and last name are required.");
      return;
    }
    setLoading(true);
    setError(null);
    const payload = {
      first_name: profile.first_name.trim(),
      last_name:  profile.last_name.trim(),
      gender:     profile.gender,
    };
    if (profile.dob) payload.dob = profile.dob;

    api
      .post("/user/update", payload)
      .then((res) => {
        if (res.data?.code !== 200) {
          setError(res.data?.message || "Couldn't save your details, try again.");
          setLoading(false);
          return;
        }
        // /user/update returns the updated profile but NOT access_token.
        // Merge the token from the original OTP login response so we don't
        // overwrite localStorage with undefined and cause 401s.
        const updatedUser = {
          ...verifiedUser,
          ...res.data.data,
          access_token: verifiedUser.access_token,
          token_type:   verifiedUser.token_type,
        };
        proceedAfterLogin(updatedUser);
      })
      .catch(() => {
        setError("Couldn't save your details, try again.");
        setLoading(false);
      });
  };

  const updateProfile = (field) => (e) =>
    setProfile((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <LabBookingLayout>
      <div className="max-w-md mx-auto bg-white rounded-2xl border border-gray-100 p-8 mt-8">

        {/* ── Step: Mobile ── */}
        {step === "mobile" && (
          <>
            <h2 className="text-[20px] font-extrabold text-[#191C1D] text-center">Welcome!</h2>
            <p className="text-[12px] text-[#6B7A99] text-center mt-1 mb-6">
              Enter your mobile number to continue
            </p>
            <label className="text-[12px] font-semibold text-[#3D4945]">Mobile Number</label>
            <input
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
              maxLength={10}
              placeholder="10-digit mobile number"
              className="w-full h-11 mt-1.5 mb-4 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
            />
            {error && <p className="text-[12px] text-red-500 mb-4">{error}</p>}
            <button
              onClick={handleSendOtp}
              disabled={loading || mobile.trim().length !== 10}
              className="w-full bg-[#13B58E] text-white text-[14px] font-semibold py-3 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
            >
              {loading ? "Please wait..." : "Send OTP"}
            </button>
          </>
        )}

        {/* ── Step: OTP ── */}
        {step === "otp" && (
          <>
            <h2 className="text-[20px] font-extrabold text-[#191C1D] text-center">Verify OTP</h2>
            <p className="text-[12px] text-[#6B7A99] text-center mt-1 mb-6">
              Sent to +91 {mobile}
            </p>
            <label className="text-[12px] font-semibold text-[#3D4945]">OTP</label>
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              maxLength={4}
              placeholder="Enter 4-digit OTP"
              className="w-full h-11 mt-1.5 mb-4 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
            />
            {error && <p className="text-[12px] text-red-500 mb-4">{error}</p>}
            <button
              onClick={handleVerify}
              disabled={loading || otp.trim().length !== 4}
              className="w-full bg-[#13B58E] text-white text-[14px] font-semibold py-3 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
            >
              {loading ? "Verifying..." : "Verify & Continue"}
            </button>
            <button
              onClick={() => { setStep("mobile"); setOtp(""); setError(null); }}
              className="w-full text-[12px] text-[#6B7A99] mt-3 hover:text-[#13B58E] transition"
            >
              Change number
            </button>
          </>
        )}

        {/* ── Step: Profile (new / incomplete users only) ── */}
        {step === "profile" && (
          <>
            <h2 className="text-[20px] font-extrabold text-[#191C1D] text-center">Almost there!</h2>
            <p className="text-[12px] text-[#6B7A99] text-center mt-1 mb-6">
              Tell us your name so your test reports are correctly labelled.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">First Name *</label>
                <input
                  value={profile.first_name}
                  onChange={updateProfile("first_name")}
                  placeholder="Rahul"
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">Last Name *</label>
                <input
                  value={profile.last_name}
                  onChange={updateProfile("last_name")}
                  placeholder="Sharma"
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">Gender</label>
                <select
                  value={profile.gender}
                  onChange={updateProfile("gender")}
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                >
                  {GENDER_OPTIONS.map((g) => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[12px] font-semibold text-[#3D4945]">Date of Birth</label>
                <input
                  type="date"
                  value={profile.dob}
                  onChange={updateProfile("dob")}
                  className="w-full h-11 mt-1.5 px-3.5 rounded-lg border border-gray-200 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
                />
              </div>
            </div>

            {error && <p className="text-[12px] text-red-500 mb-4">{error}</p>}

            <button
              onClick={handleSaveProfile}
              disabled={loading || !profile.first_name.trim() || !profile.last_name.trim()}
              className="w-full bg-[#13B58E] text-white text-[14px] font-semibold py-3 rounded-full hover:bg-[#0fa07d] transition disabled:opacity-60"
            >
              {loading ? "Saving..." : "Save & Continue"}
            </button>
          </>
        )}

        <p className="text-[11px] text-[#9AA6A4] text-center mt-4">
          By continuing, you agree to Healthism's Terms of Service and Privacy Policy
        </p>
      </div>
    </LabBookingLayout>
  );
};

export default LoginPage;
